<template>
    <div
        class="kevel-ad-sponsored block relative py-9 px-5 bg-base-250 flex flex-col gap-2"
        :class="{ 'cursor-pointer': isValidTarget }"
        :role="isValidTarget ? 'button' : undefined"
        :tabindex="isValidTarget ? 0 : undefined"
        v-on="isValidTarget ? {
            click: handleContainerClick,
            'keydown.enter': handleContainerClick,
            'keydown.space': (e: KeyboardEvent) => { e.preventDefault(); handleContainerClick(e); }
        } : {}"
    >

        <div class="flex justify-between">
            <!-- Headline + Subline -->
            <div class="flex flex-col gap-2 text-base-content">

                <!-- Headline -->
                <h2 v-if="ad.headline" class="text-xl md:text-3xl font-semibold">
                    {{ ad.headline }}
                </h2>

                <!-- Subline -->
                <p v-if="ad.subline" class="text-base ">
                    {{ ad.subline }}
                </p>

                <div class="link text-xs pt-[2px] mb-4">{{ t('kevel.ad.discoverMore') }}</div>
            </div>

            <!-- Sponsored Label -->
            <div class="">
                <p class="text-xs uppercase font-normal text-base-content-subtle tracking-[0.5px]">
                    {{ t('kevel.ad.label') }}
                </p>
            </div>
        </div>

        <div class="flex items-start gap-4">
            <!-- Brand Asset: stretches to the slider's height (the image is absolute, so it doesn't
                 add height of its own); falls back to 4:5 when there is no slider. min-w-0: otherwise
                 the flex item won't shrink below its content width -->
            <div
                v-if="ad.brandAsset"
                class="relative w-full min-w-0 xl:max-w-[292px]"
                :class="hasProductsColumn ? 'self-stretch' : 'aspect-[4/5]'"
            >
                <MiscImage
                    :src="ad.brandAsset"
                    :alt="ad.headline ?? t('kevel.ad.defaultAlt')"
                    class="absolute inset-0 w-full h-full object-cover"
                    :lazy-load="!aboveTheFold"
                />
            </div>

            <!-- Products Carousel -->
            <div
                v-if="!productsLoading && listingProducts.length > 0"
                class="-mt-15 w-full overflow-x-hidden"
                @click.stop
            >
                <ProductSlider
                    :products="listingProducts"
                    :product-qty="{ value: '6', source: 'static' }"
                    :slides-per-view="{
                        xs: 1,
                        sm: 1,
                        md: 1,
                        lg: 1,
                        xl: 3
                    }"
                    :space-between="16"
                    :display-nav="true"
                    variant="full"
                    slider-id="kevel-sponsored"
                    slider-title="Sponsored Products"
                    item-list-id="kevel_sponsored_products"
                    item-list-name="Kevel Sponsored Products"
                />
            </div>

            <!-- Loading placeholder for products -->
            <div v-else-if="productsLoading" class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <MiscSkeleton v-for="i in 6" :key="i" class="aspect-square" />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { ParsedKevelAd } from '../../utils/parseKevelDecision'
import { loadKevelProducts } from '../../utils/loadKevelProducts'
import type { Schemas } from '#shopware'

interface Props {
    ad: ParsedKevelAd
    aboveTheFold?: boolean
    trackingComponent?: string
}

const props = withDefaults(defineProps<Props>(), {
    aboveTheFold: false,
    trackingComponent: 'plp-sponsored'
})

const { t } = useI18n()

// Kevel tracking (impression + click)
const { trackClick, isValidTarget } = useKevelAdTracking(props.ad, {
    component: props.trackingComponent,
})

// Load products if product numbers are provided
const productsLoading = ref(false)
const products = ref<Schemas['Product'][]>([])

onMounted(async () => {
    if (props.ad.products != null && props.ad.products.length > 0) {
        productsLoading.value = true
        try {
            // Load up to 16 products
            const productNumbers = props.ad.products.slice(0, 16)
            products.value = await loadKevelProducts(productNumbers)
        } catch (error) {
            console.error('[kevel] Failed to load sponsored products:', error)
        } finally {
            productsLoading.value = false
        }
    }
})

// Convert products to ProductSlider format
const listingProducts = computed(() => {
    return products.value.map((product, index) => ({
        pos: index,
        type: 'product' as const,
        data: product
    }))
})

// Slider or its loading placeholder is shown, so the brand asset can take its height
const hasProductsColumn = computed(() => productsLoading.value || listingProducts.value.length > 0)

// Container click handler
function handleContainerClick(event: MouseEvent | KeyboardEvent) {
    if (isValidTarget) {
        trackClick(event instanceof MouseEvent ? event : undefined)
    }
}
</script>
